import React from 'react';
import { Settings, AlertTriangle, Eye, FileText } from 'lucide-react';

interface AboutProps {
  isDarkMode?: boolean;
}

const About: React.FC<AboutProps> = ({ isDarkMode = false }) => {
  const features = [
    {
      icon: Settings,
      title: "Advanced Threat Detection",
      description: "AI-powered security analytics and machine learning algorithms to identify and neutralize sophisticated cyber threats before they impact your business."
    },
    {
      icon: AlertTriangle,
      title: "Compliance Management",
      description: "Comprehensive compliance solutions for GDPR, ISO 27001, SOC 2, and industry-specific regulations with automated reporting and audit trails."
    },
    {
      icon: Eye,
      title: "Real-time Monitoring",
      description: "24/7 security operations center with expert analysts monitoring your infrastructure using cutting-edge SIEM and threat intelligence platforms."
    },
    {
      icon: FileText,
      title: "Risk Assessment",
      description: "Thorough security assessments, vulnerability testing, and risk analysis to identify weaknesses and strengthen your security posture."
    }
  ];

  return (
    <section id="about" className={`py-20 ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>
              Why Choose CyberSoft?
            </h2>
            <p className={`text-lg mb-8 ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              With over a decade of experience in cybersecurity, CyberSoft has established 
              itself as a trusted partner for enterprises seeking comprehensive security solutions. 
              Our team of certified security professionals combines deep technical expertise 
              with strategic business understanding.
            </p>
            <div className="space-y-6">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <feature.icon className={`h-6 w-6 mt-1 flex-shrink-0 ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                  <div>
                    <h3 className={`font-semibold mb-1 ${
                      isDarkMode ? 'text-slate-100' : 'text-slate-900'
                    }`}>{feature.title}</h3>
                    <p className={`text-sm ${
                      isDarkMode ? 'text-slate-300' : 'text-slate-600'
                    }`}>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className={`p-8 rounded-xl ${
            isDarkMode ? 'bg-slate-900' : 'bg-slate-50'
          }`}>
            <h3 className={`text-2xl font-bold mb-6 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>Our Certifications</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                "ISO 27001 Certified",
                "CISSP Professionals",
                "SOC 2 Type II",
                "CISA Certified",
                "PCI DSS Compliant",
                "GDPR Compliant"
              ].map((cert, index) => (
                <div key={index} className={`p-4 rounded-lg text-center ${
                  isDarkMode ? 'bg-slate-800' : 'bg-white'
                }`}>
                  <div className={`text-sm font-semibold ${
                    isDarkMode ? 'text-slate-100' : 'text-slate-900'
                  }`}>{cert}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;